import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function OldhamCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Oldham Pothole Claims | GREEN Roads, RED Best Practice | Fixtyer"
        description="Oldham Council earns GREEN for road condition and spend from the DfT, yet RED for wider best practice — dragging the overall rating to AMBER. 668km of U-roads, 973 pothole work orders in 2024/25, and 46% reactive spend. See what it means for your claim."
        canonical="/councils/oldham"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Oldham', url: '/councils/oldham' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Oldham is GREEN on condition and spend — can I still claim for pothole damage?',
            answer: 'Yes. GREEN scorecards reflect aggregate network condition and capital spend matching the DfT allocation — not a guarantee that your specific defect was reasonably inspected and repaired. Oldham is AMBER overall because the DfT rates its wider best practice RED. Section 58 turns on the particular pothole, prior reports, and whether the council\'s maintenance system — not its headline colours — was reasonable for that location.',
          },
          {
            question: 'What does the RED best practice rating mean for my pothole claim?',
            answer: 'The DfT wider best practice scorecard measures preventative spend share, planned resurfacing against RED-rated roads, innovation adoption, and evidence of disruption, decarbonisation and footway plans. Oldham projects 54% preventative and 46% reactive maintenance in 2025/26, plans just 3km of resurfacing and 4.1km of preventative treatment on an 846km network, and admits waste-free repair technology is "only used in targeted locations" due to expense. That gap between GREEN condition data and RED process assessment is directly relevant to Section 58.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 668km — 79% of Oldham\'s 846km carriageway network. At the latest SCANNER survey, 9% of U-roads were in RED condition, 38% in amber and 53% in green — with green coverage falling from 61% in 2023 to 53% in 2024. Most pothole claims in Oldham originate on these local streets, not the 112km of A-roads where condition scores are strongest.',
          },
          {
            question: 'Pothole repairs fell to 973 work orders in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Oldham completed 973 pothole repair work orders in 2024/25 — down from 1,443 the year before and 3,350 in 2020/21. The council itself notes that one work order may repair multiple defects, so the true pothole count is higher. It also plans an estimated 1,000 pothole repairs in 2025/26. Fewer work orders does not mean fewer potholes — it reflects repair activity, not network health.',
          },
          {
            question: 'Does Oldham\'s asset management strategy block my Section 58 argument?',
            answer: 'Not necessarily — but it sets the terms of the fight. Oldham publishes a Highway Asset Management Policy and claims methodologies follow Well Managed Highway Infrastructure guidance. That gives the council a documented system to point to. Your claim succeeds by showing that system failed for your specific defect: prior reports ignored, a pothole visible longer than inspection intervals allow, or a RED-classified U-road left untreated despite SCANNER data.',
          },
          {
            question: 'Why is Oldham AMBER overall when two scorecards are GREEN?',
            answer: 'The DfT combines condition, spend and best practice into a single overall score with a higher bar for amber than any individual scorecard. Oldham\'s GREEN condition reflects SCANNER survey results across A, B/C and U-roads. Its GREEN spend reflects capital matching the £3.067m DfT allocation exactly. But RED best practice — driven by low planned preventative works relative to network size and a 46% reactive spend share — pulls the combined rating down to AMBER.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Oldham Council"
        councilSlug="oldham"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/oldham"
        headline="Oldham Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Oldham</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Oldham: GREEN Roads, RED Processes
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Oldham Council's SCANNER surveys earn <strong className="text-emerald-400">GREEN</strong> for road
            condition. Its capital spend matches the DfT allocation for a <strong className="text-emerald-400">GREEN</strong> spend
            scorecard. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — because the DfT
            rates wider best practice <strong className="text-rose-400">RED</strong>. The roads measure well; the
            maintenance processes do not.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">RED</div>
            <div className="text-white font-medium mt-2">Wider best practice scorecard</div>
            <div className="text-brand-300 text-sm mt-1">
              Oldham projects 46% reactive maintenance in 2025/26, plans 3km of resurfacing on an 846km network,
              and limits waste-free pothole technology to "targeted locations" — while condition and spend both
              score GREEN.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The RED Best Practice Gap */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The RED Best Practice Scorecard
          </h2>
          <p className="text-surface-600 mb-8">
            Why GREEN condition and spend still produce an AMBER council — the DfT process test Oldham fails
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Due to its expense, these waste-free products are only used in targeted locations, however they have had a positive impact on pothole reduction as they arrest further deterioration of the surface and prevent new potholes from forming adjacent to the repairs."
              source="Oldham Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="We are also looking to be a Lane Rental authority where we can charge a daily rate for works on our busiest roads during the busiest times. This should incentivise organisations that undertake roadworks by making them think about how they intend to carry out their works and look for innovative ways to avoid the charge by using no dig techniques, collaborating with other roadworks or working outside of the peak periods."
              source="Oldham Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">What RED Means</h3>
              <p className="text-rose-800 text-sm mb-3">
                The DfT wider best practice scorecard assesses preventative versus reactive spend, planned
                resurfacing of RED-rated roads, planned preventative treatment of green and amber roads, and
                evidence of innovation, disruption management, decarbonisation and footway maintenance plans.
                A RED rating means Oldham is among the lowest performers nationally on those process measures —
                despite GREEN condition data from its own SCANNER surveys.
              </p>
              <p className="text-rose-800 text-sm">
                For Section 58, that matters: the council cannot lean on official best-practice recognition while
                arguing its maintenance system was reasonable. The gap between what the roads measure and how
                the council maintains them is yours to explore.
              </p>
            </div>

            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Numbers Behind RED</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• 2025/26 planned spend split: 54% preventative, 46% reactive</li>
                <li>• 2025/26 planned works: 3km resurfacing + 4.1km preventative treatment on 846km of roads</li>
                <li>• 2024/25 actual resurfacing: 8.45km carriageway — down from 47.12km in 2021/22</li>
                <li>• Waste-free pothole repair: limited to "targeted locations" due to cost</li>
                <li>• Lane Rental scheme: aspiration ("looking to be"), not yet in place</li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 58 and Section 41 Highways Act</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Oldham must maintain its roads. Under Section 58, it
              can defend a claim only if it proves a reasonable inspection and repair system — and that the
              specific defect was not actionable under that system.
            </p>
            <p className="text-white font-medium">
              A RED best practice scorecard does not automatically win your claim — but it undercuts any blanket
              assertion that Oldham runs a modern, efficient maintenance regime. Pair it with U-road condition
              data, falling pothole repair volumes, and your own evidence of prior reports.
            </p>
          </div>
        </div>
      </section>

      {/* Network size */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 846km Network
          </h2>
          <p className="text-surface-600 mb-8">
            Oldham's own network breakdown — and where your pothole likely sits
          </p>

          <QuoteCard
            quote="Oldham Council manages and maintains adopted highway assets falling within its 846km network."
            source="Oldham Council Local Highways Maintenance Transparency Report 2025"
          />

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8 mt-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of carriageway</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">112</td>
                  <td className="px-6 py-4 text-center">13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">66</td>
                  <td className="px-6 py-4 text-center">8%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-bold text-amber-900">Unclassified roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">668</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-800">79%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Footways</td>
                  <td className="px-6 py-4 text-center font-mono">1,099</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Public Rights of Way</td>
                  <td className="px-6 py-4 text-center font-mono">450</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-brand-900">Cycleways</td>
                  <td className="px-6 py-4 text-center font-mono">56</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Not All Oldham Roads</h3>
              <p className="text-surface-600 text-sm">
                The council does not maintain motorways or A663 Broadway in Chadderton (Manchester boundary
                to A627(M)) — a trunk road managed by National Highways. Street lights and illuminated signs
                are managed by Community Lighting Partnership under a PFI contract.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Where Claims Happen</h3>
              <p className="text-amber-800 text-sm">
                Nearly four-fifths of Oldham's carriageway is unclassified — estate roads, residential streets
                and local routes. These are where pothole damage claims concentrate, even though A-roads carry
                the best condition scores in the SCANNER data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Oldham's own transparency report — GREEN overall, but
            U-road amber rising
          </p>

          <QuoteCard
            quote="Road condition assessments on the local classified road network in England are currently made predominantly using surface condition assessment for the national network of roads (SCANNER) laser-based technology."
            source="Oldham Council Local Highways Maintenance Transparency Report 2025"
          />

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6 mt-8">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (112km — 13% of network): strong green coverage</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">10%</td>
                    <td className="px-4 py-2 text-center">84%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">13%</td>
                    <td className="px-4 py-2 text-center">85%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center">83%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">1%</td>
                    <td className="px-4 py-2 text-center font-bold">28%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">71%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition is just 1% at the latest survey — but amber has risen from 10% to 28% since
              2020. Main roads perform well; they are also just one-eighth of the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (66km — 8% of network): RED ticked up in 2024</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">13%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">69%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">69%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">13%</td>
                    <td className="px-4 py-2 text-center">83%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">17%</td>
                    <td className="px-4 py-2 text-center">81%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold">22%</td>
                    <td className="px-4 py-2 text-center font-bold">72%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C RED condition tripled from 2% to 6% between 2023 and 2024 — on a small but strategically
              important 66km slice of the borough's classified network.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (668km — 79% of network): green slipping, amber rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">14%</td>
                    <td className="px-4 py-2 text-center">35%</td>
                    <td className="px-4 py-2 text-center">51%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">34%</td>
                    <td className="px-4 py-2 text-center">55%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">58%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">61%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">9%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">38%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">53%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              U-road green coverage fell from 61% to 53% in a single year while amber rose from 28% to 38%.
              At 9% RED, roughly 60km of residential and local roads are classified for resurfacing — yet
              Oldham resurfaced just 8.45km of carriageway across the entire borough in 2024/25.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — Matching The Allocation Exactly</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3.067m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3.067m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.66m</div>
                <div className="text-brand-300 text-sm">Total projected spend (capital + revenue)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Oldham matches its DfT capital allocation pound for pound — unlike authorities that borrow or
              top up heavily. Total maintenance spend in 2024/25 was £5.575m (£3.067m capital plus £2.508m
              revenue). GREEN spend reflects that alignment, not whether your specific pothole was caught in time.
            </p>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital, revenue, and the shift from Highways Investment Programme borrowing to static DfT-matched budgets
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative %</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£4.714m</td>
                  <td className="px-4 py-3 text-center">£9.414m*</td>
                  <td className="px-4 py-3 text-center">£1.528m†</td>
                  <td className="px-4 py-3 text-center text-emerald-600">86</td>
                  <td className="px-4 py-3 text-center">14</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£4.714m</td>
                  <td className="px-4 py-3 text-center">£8.272m*</td>
                  <td className="px-4 py-3 text-center">£1.983m†</td>
                  <td className="px-4 py-3 text-center text-emerald-600">81</td>
                  <td className="px-4 py-3 text-center">19</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£3.067m</td>
                  <td className="px-4 py-3 text-center">£3.067m</td>
                  <td className="px-4 py-3 text-center">£2.459m</td>
                  <td className="px-4 py-3 text-center">56</td>
                  <td className="px-4 py-3 text-center">44</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£3.067m</td>
                  <td className="px-4 py-3 text-center">£3.067m</td>
                  <td className="px-4 py-3 text-center">£2.739m</td>
                  <td className="px-4 py-3 text-center">53</td>
                  <td className="px-4 py-3 text-center">47</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£3.067m</td>
                  <td className="px-4 py-3 text-center">£3.067m</td>
                  <td className="px-4 py-3 text-center">£2.508m</td>
                  <td className="px-4 py-3 text-center">55</td>
                  <td className="px-4 py-3 text-center">45</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26</td>
                  <td className="px-4 py-3 text-center">£3.067m</td>
                  <td className="px-4 py-3 text-center">£3.067m</td>
                  <td className="px-4 py-3 text-center">£2.593m‡</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-600">54</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">46</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-sm text-surface-600">
              <p className="mb-2">* Figures for 2020/21 &amp; 2021/22 include additional funding via prudential borrowing which formed Oldham&apos;s Highways Investment Programme (HIP)</p>
              <p className="mb-2">† Reduced service due to Covid</p>
              <p>‡ Forecast based on month 3 figures</p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">The Preventative Cliff</h3>
              <p className="text-rose-800 text-sm">
                During the HIP borrowing years, Oldham classified 81–86% of spend as preventative. Since
                2022/23, that share has sat at 53–56% — with reactive maintenance at 44–47%. The DfT best
                practice metric rewards high preventative shares; Oldham&apos;s current split is a key driver
                of its RED scorecard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            10,057 Pothole Work Orders in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes falling — but the network still produces defects
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Pothole work orders completed</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold">3,350</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold">2,861</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,430</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">1,443</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">973</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10,057</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <QuoteCard
              quote="Please note that the numbers provided represent the number of completed work orders raised for pothole repairs to road surfaces and one order may repair multiple defects."
              source="Oldham Council Local Highways Maintenance Transparency Report 2025"
            />
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex flex-col justify-center">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 Forecast: 1,000 Repairs</h3>
              <p className="text-amber-800 text-sm">
                Oldham plans an estimated 1,000 pothole repairs in 2025/26 — nearly double the 973 work orders
                completed in 2024/25. The council notes actual repairs will be based on need.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Resurfacing vs Reactive Patch Cycle</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-rose-400">8.45km</div>
                <div className="text-brand-300 text-sm">Carriageway resurfaced 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">47.12km</div>
                <div className="text-brand-300 text-sm">Carriageway resurfaced 2021/22 (HIP year)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">3km</div>
                <div className="text-brand-300 text-sm">Planned resurfacing 2025/26</div>
              </div>
            </div>
            <p className="text-brand-200">
              Oldham resurfaced 47.12km during its Highways Investment Programme — then 15.0km, 12.8km, 8.45km
              and plans just 3km for 2025/26. When structural maintenance slows while U-road amber rises to 38%,
              potholes form on roads the council already knows need attention. That is the reactive cycle Section
              58 asks you to pin to a specific defect.
            </p>
          </div>
        </div>
      </section>

      {/* Asset management and plans */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Asset Management — Paper vs Practice
          </h2>
          <p className="text-surface-600 mb-8">
            What Oldham says about its processes — and where the DfT found them wanting
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Oldham Council is committed to a highway asset management approach for the long-term maintenance if its highway infrastructure assets. Methodologies follow national guidance and best practice, including the recommendations set out in Well Managed Highway Infrastructure, the code of practice for the management of highway assets."
              source="Oldham Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Oldham Council employs a preventative maintenance approach to its asset maintenance strategy to apply treatments to its assets at key stages along its lifecycle. This maximises the lifespan of our assets, reducing the need for otherwise more carbon-intensive replacements further down the line."
              source="Oldham Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What The Report Claims</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Highway Asset Management Policy, Framework and Strategy published online</li>
                <li>• Greater Manchester collaboration via GMRAPS, Highways Asset Manager&apos;s Group, shared Pontis for structures</li>
                <li>• Waste-free pothole repair trialled and rolled out to targeted locations</li>
                <li>• Fleet transitioning to cleaner vehicles; SUDS incorporated in schemes</li>
                <li>• Manchester Street viaduct refurbishment — final phase of Oldham Way structure works</li>
              </ul>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">What The DfT Found</h3>
              <ul className="text-rose-800 text-sm space-y-2">
                <li>• RED wider best practice — lowest tier on efficiency and innovation measures</li>
                <li>• 54% preventative / 46% reactive projected split in 2025/26</li>
                <li>• 3km resurfacing + 4.1km preventative treatment planned on 846km network</li>
                <li>• Innovation limited: waste-free repair &quot;only used in targeted locations&quot; due to expense</li>
                <li>• Lane Rental: council is &quot;looking to be&quot; an authority — not yet implemented</li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 58 Questions For Oldham</h3>
            <p className="text-brand-200 mb-4">
              Oldham publishes policies and participates in Greater Manchester frameworks. Under Section 58, ask
              whether that system worked for your road:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your U-road in the 9% RED or 38% amber category at the last SCANNER survey?</li>
              <li>• If so, why was it not on the 3km resurfacing or 4.1km preventative treatment list for 2025/26?</li>
              <li>• Did prior reports to the council pre-date the repair — proving actual notice?</li>
              <li>• Does the pothole show signs of age (weathered edges, previous patching) inconsistent with inspection intervals?</li>
              <li>• How does a RED best practice scorecard sit alongside the council&apos;s asset management claims?</li>
            </ul>
            <p className="text-white font-medium">
              GREEN condition data helps the council. RED best practice data helps you — if you connect it to
              your specific defect with photos and prior reports.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Split-Rated Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Oldham is not Derbyshire — here&apos;s how the mixed scorecards change your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard — SCANNER data shows strong green coverage on A-roads (71%) and manageable RED on U-roads (9%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital matches £3.067m DfT allocation exactly
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published asset management policy aligned to Well Managed Highway Infrastructure
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Greater Manchester coordination via GMRAPS and shared regional frameworks
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED share fell from 14% (2020) to 9% (2024)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence citing SCANNER surveys and asset management policies. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED wider best practice — DfT found processes lacking despite GREEN condition data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 46% reactive maintenance projected in 2025/26 — nearly half the budget on patch-and-fill
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road green fell from 61% to 53% in one year; amber rose to 38%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing collapsed from 47.12km (2021/22) to 8.45km (2024/25) after HIP borrowing ended
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Waste-free repair technology limited to &quot;targeted locations&quot; — not borough-wide
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 79% of the network is U-roads — where condition is weakest and claims concentrate
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN condition and spend scorecards, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, cite the 38% amber and falling green coverage</li>
              <li>• The gap between SCANNER RED/amber classification and planned 3km resurfacing list</li>
              <li>• The RED best practice scorecard as context for whether the council&apos;s stated system matches DfT assessment</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Oldham&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Oldham?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            GREEN scorecards demand a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No RED best practice argument</li>
                <li>• No U-road condition decline cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ RED best practice scorecard cited</li>
                <li>✅ U-road amber rise and green fall documented</li>
                <li>✅ 10,057 pothole work orders in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Oldham</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=oldham"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Oldham Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Oldham is GREEN on condition and spend — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. GREEN scorecards reflect aggregate network condition and capital spend matching the DfT
                allocation — not a guarantee that your specific defect was reasonably inspected and repaired.
                Oldham is AMBER overall because the DfT rates its wider best practice RED. Section 58 turns on
                the particular pothole, prior reports, and whether the council&apos;s maintenance system — not
                its headline colours — was reasonable for that location.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the RED best practice rating mean for my pothole claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT wider best practice scorecard measures preventative spend share, planned resurfacing
                against RED-rated roads, innovation adoption, and evidence of disruption, decarbonisation and
                footway plans. Oldham projects 54% preventative and 46% reactive maintenance in 2025/26, plans
                just 3km of resurfacing and 4.1km of preventative treatment on an 846km network, and admits
                waste-free repair technology is &quot;only used in targeted locations&quot; due to expense. That gap
                between GREEN condition data and RED process assessment is directly relevant to Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 668km — 79% of Oldham&apos;s 846km carriageway network. At the latest
                SCANNER survey, 9% of U-roads were in RED condition, 38% in amber and 53% in green — with green
                coverage falling from 61% in 2023 to 53% in 2024. Most pothole claims in Oldham originate on
                these local streets, not the 112km of A-roads where condition scores are strongest.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell to 973 work orders in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Oldham completed 973 pothole repair work orders in 2024/25 — down from 1,443 the year before
                and 3,350 in 2020/21. The council itself notes that one work order may repair multiple defects, so
                the true pothole count is higher. It also plans an estimated 1,000 pothole repairs in 2025/26.
                Fewer work orders does not mean fewer potholes — it reflects repair activity, not network health.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Oldham&apos;s asset management strategy block my Section 58 argument?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily — but it sets the terms of the fight. Oldham publishes a Highway Asset Management
                Policy and claims methodologies follow Well Managed Highway Infrastructure guidance. That gives the
                council a documented system to point to. Your claim succeeds by showing that system failed for
                your specific defect: prior reports ignored, a pothole visible longer than inspection intervals
                allow, or a RED-classified U-road left untreated despite SCANNER data.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Oldham AMBER overall when two scorecards are GREEN?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT combines condition, spend and best practice into a single overall score with a higher bar
                for amber than any individual scorecard. Oldham&apos;s GREEN condition reflects SCANNER survey results
                across A, B/C and U-roads. Its GREEN spend reflects capital matching the £3.067m DfT allocation
                exactly. But RED best practice — driven by low planned preventative works relative to network size
                and a 46% reactive spend share — pulls the combined rating down to AMBER.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.oldham.gov.uk/highways_maintenance_transparency_report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Oldham Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
